import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ImageDrop } from "./HomeContent";
import FontSettings from "./FontSettings";

export function LandingHero({ title, subtitle, heroImages, heroImageUrl, rotateSeconds = 5 }) {
  const images = (heroImages && heroImages.length ? heroImages : [heroImageUrl]).filter(Boolean);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [images.length]);
  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), Math.max(2, rotateSeconds) * 1000);
    return () => clearInterval(t);
  }, [images.length, rotateSeconds]);
  return (
    <div className="relative h-52 overflow-hidden rounded-[28px]" data-testid="landing-hero">
      {images.map((src, i) => (
        <img key={`${i}-${src.slice(0, 40)}`} src={src} alt="Fresh flowers" data-testid={i === idx ? "landing-hero-image" : undefined}
          className={`absolute inset-0 h-52 w-full object-cover transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-[#D46117] via-[#D46117]/50 to-black/10" />
      <div className="absolute inset-0 flex flex-col justify-center px-6">
        <h1 className="whitespace-pre-line font-serif text-5xl font-extrabold leading-[0.95] text-white drop-shadow" data-testid="landing-hero-title">{title}</h1>
        <p className="mt-3 text-sm font-medium text-white/90" data-testid="landing-hero-subtitle">{subtitle}</p>
      </div>
      {images.length > 1 && (
        <div className="absolute bottom-3 right-4 flex gap-1.5" data-testid="landing-hero-dots">
          {images.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/50"}`} />)}
        </div>
      )}
    </div>
  );
}

export default function LandingPage() {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  useEffect(() => {
    adminApi.getLanding().then(setDraft).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      setDraft(await adminApi.saveLanding({ title: draft.title, subtitle: draft.subtitle, hero_images: draft.hero_images, rotate_seconds: Number(draft.rotate_seconds) || 5 }));
      toast.success("Landing page saved — live on the store login now");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  if (!draft) return <div className="grid place-items-center py-20 text-[#28282E]/55"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  const imgs = draft.hero_images || [];
  const setImg = (i, v) => set({ hero_images: imgs.map((x, j) => (j === i ? v : x)) });
  const move = (i, d) => { const n = [...imgs]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; set({ hero_images: n }); };

  return (
    <div data-testid="admin-landing-page">
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-bold text-[#28282E]">Landing Page</h1>
        <p className="text-sm text-[#28282E]/70">Edit the hero shown on the IGA store login screen. Changes go live immediately.</p>
      </div>
      <div className="grid gap-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:grid-cols-2">
        <div className="space-y-4">
          <Label className="block text-xs font-semibold text-[#28282E]/70">Live preview</Label>
          <div className="bg-terracotta rounded-[32px] p-4">
            <LandingHero title={draft.title} subtitle={draft.subtitle} heroImages={imgs} rotateSeconds={Number(draft.rotate_seconds) || 5} />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Main title (one line per row)</Label>
            <Textarea rows={3} value={draft.title || ""} onChange={(e) => set({ title: e.target.value })} data-testid="landing-title-input" />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-semibold text-[#28282E]/70">Subtitle</Label>
            <Input value={draft.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} data-testid="landing-subtitle-input" />
          </div>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="block text-xs font-semibold text-[#28282E]/70">Hero photos ({imgs.length}) — rotate on the login screen</Label>
            <div className="flex items-center gap-2 text-xs text-[#28282E]/70">Every <Input type="number" min={2} max={60} value={draft.rotate_seconds ?? 5} onChange={(e) => set({ rotate_seconds: e.target.value })} data-testid="landing-rotate-seconds" className="h-7 w-16 text-xs" /> sec</div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {imgs.map((u, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 p-2" data-testid={`landing-hero-slot-${i}`}>
                <ImageDrop value={u} onChange={(v) => setImg(i, v)} testid={`landing-hero-${i}`} />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[11px] font-bold text-[#28282E]/60">#{i + 1}</span>
                  <div className="flex gap-1">
                    <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`landing-hero-${i}-left`} className="grid h-7 w-7 place-items-center rounded-lg border border-slate-200 disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === imgs.length - 1} data-testid={`landing-hero-${i}-right`} className="grid h-7 w-7 place-items-center rounded-lg border border-slate-200 disabled:opacity-30"><ChevronRight className="h-3.5 w-3.5" /></button>
                    <button onClick={() => set({ hero_images: imgs.filter((_, j) => j !== i) })} disabled={imgs.length === 1} data-testid={`landing-hero-${i}-remove`} className="grid h-7 w-7 place-items-center rounded-lg border border-red-200 text-red-600 disabled:opacity-30"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
            <button onClick={() => set({ hero_images: [...imgs, ""] })} data-testid="landing-hero-add" className="flex aspect-video items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 text-sm font-semibold text-[#28282E]/60 hover:border-[#EBBAD1] hover:text-[#28282E]"><Plus className="h-4 w-4" /> Add photo</button>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => set({ title: "Hasfarm\nOrders\nPortal", subtitle: "Fresh flowers, ordered in seconds." })} data-testid="landing-reset-text"
              className="rounded-full border-[#EBBAD1] text-[#28282E] hover:bg-[#FBEEF4]"><RotateCcw className="mr-2 h-4 w-4" /> Reset text</Button>
            <Button onClick={save} disabled={saving} data-testid="landing-save-button" className="rounded-full bg-[#28282E] px-6 text-white hover:bg-[#1F1F24]">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save changes
            </Button>
          </div>
        </div>
      </div>

      <FontSettings />
    </div>
  );
}
